import React, { useState } from 'react';
import { Users, Building2, Calendar, MoreVertical, UserCheck, UserX, Shield, Mail, Trash2, UserPlus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import CreateUserModal from './CreateUserModal';

export default function UserManagementPanel({ users, onRefresh, companies = [] }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActions, setShowActions] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const handleUserStatusToggle = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: !currentStatus })?.eq('id', userId);

      if (error) throw error;

      onRefresh?.();
      setShowActions(null);
    } catch (error) {
      console.error('Error updating user status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ role: newRole })?.eq('id', userId);

      if (error) throw error;

      onRefresh?.();
      setShowActions(null);
    } catch (error) {
      console.error('Error updating user role:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      // Delete user profile first (will cascade to auth via triggers)
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId);

      if (error) throw error;

      onRefresh?.();
      setShowDeleteConfirm(null);
      setShowActions(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateUser = (newUser) => {
    // Close modal and refresh data
    setShowCreateUser(false);
    onRefresh?.();
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'company_admin':
        return 'bg-purple-100 text-purple-800';
      case 'company_user':
        return 'bg-blue-100 text-blue-800';
      case 'platform_admin':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'company_admin':
        return Shield;
      case 'company_user':
        return Users;
      case 'platform_admin':
        return Shield;
      default:
        return Users;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header with Add User Button */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
            <p className="text-sm text-gray-500">Manage users across all companies</p>
          </div>
          <button
            onClick={() => setShowCreateUser(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {users?.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
          <p className="text-gray-500 mb-4">No users match your search criteria.</p>
          <button
            onClick={() => setShowCreateUser(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center space-x-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Create First User</span>
          </button>
        </div>
      ) : (
        <div className="overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">User</div>
              <div className="col-span-3">Company</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Joined</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {users?.map((user, index) => {
              const RoleIcon = getRoleIcon(user?.role);
              const isLoading = actionLoading === user?.id;
              
              return (
                <div key={user?.id || index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* User Info */}
                    <div className="col-span-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user?.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user?.full_name}</div>
                          <div className="text-sm text-gray-500 flex items-center space-x-1">
                            <Mail className="w-3 h-3" />
                            <span>{user?.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Company */}
                    <div className="col-span-3">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {user?.companies?.name || 'Platform Admin'}
                        </span>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <RoleIcon className="w-4 h-4 text-gray-400" />
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user?.role)}`}>
                          {user?.role?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-1">
                      <div className="flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          user?.is_active ? 'bg-green-400' : 'bg-red-400'
                        }`}></span>
                        <span className="text-sm text-gray-600">
                          {user?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {/* Joined Date */}
                    <div className="col-span-1">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {formatDate(user?.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 relative">
                      {isLoading ? (
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <button
                          onClick={() => setShowActions(showActions === user?.id ? null : user?.id)}
                          className="p-1 rounded hover:bg-gray-100 transition-colors"
                          disabled={user?.role === 'platform_admin'}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                      )}

                      {showActions === user?.id && user?.role !== 'platform_admin' && (
                        <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-48">
                          {/* Status Toggle */}
                          <button 
                            onClick={() => handleUserStatusToggle(user?.id, user?.is_active)}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                              user?.is_active ? 'text-red-700' : 'text-green-700'
                            }`}
                          >
                            {user?.is_active ? (
                              <>
                                <UserX className="w-4 h-4" />
                                <span>Deactivate User</span>
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4" />
                                <span>Activate User</span>
                              </>
                            )}
                          </button>

                          <div className="border-t border-gray-200 my-1"></div>

                          {/* Role Changes */}
                          {user?.role !== 'company_admin' && (
                            <button 
                              onClick={() => handleRoleChange(user?.id, 'company_admin')}
                              className="w-full px-4 py-2 text-left text-sm text-purple-700 hover:bg-purple-50 flex items-center space-x-2"
                            >
                              <Shield className="w-4 h-4" />
                              <span>Make Admin</span>
                            </button>
                          )}

                          {user?.role !== 'company_user' && (
                            <button 
                              onClick={() => handleRoleChange(user?.id, 'company_user')}
                              className="w-full px-4 py-2 text-left text-sm text-blue-700 hover:bg-blue-50 flex items-center space-x-2"
                            >
                              <Users className="w-4 h-4" />
                              <span>Make User</span>
                            </button>
                          )}

                          <div className="border-t border-gray-200 my-1"></div>

                          {/* Delete User */}
                          <button 
                            onClick={() => setShowDeleteConfirm(user?.id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete User</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }) || (
              <div className="px-6 py-8 text-center text-gray-500">
                No users to display
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this user? This will permanently remove their account and all associated data.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(showDeleteConfirm)}
                disabled={actionLoading === showDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {actionLoading === showDeleteConfirm ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete User</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreate={handleCreateUser}
          companies={companies}
        />
      )}
    </div>
  );
}